/**
 * Tracked Insights Sheet
 * Shows user's tracked/bookmarked insights
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import {
  X,
  Bookmark,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  RefreshCw,
} from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { InsightDetailSheet } from './InsightDetailSheet'
import {
  useTrackedInsights,
  useUntrackInsight,
  useStockQuotes,
  useUpdateEntryPrice,
} from '@/hooks'
import { calculateGainLoss } from '@/lib/services/stockPrice'
import { cn } from '@/lib/utils'
import { TranslatableText } from '@/components/TranslatableText'
import { getDateLocale } from '@/lib/utils/dateLocale'
import { toast } from 'sonner'
import type { TrackedInsight } from '@/lib/repositories'

interface TrackedInsightsSheetProps {
  open: boolean
  onClose: () => void
  onAskAI?: (
    insightId: string,
    ticker: string | undefined,
    sentiment: string,
    content: string
  ) => void
}

export function TrackedInsightsSheet({
  open,
  onClose,
  onAskAI,
}: TrackedInsightsSheetProps) {
  const { t, i18n } = useTranslation()
  const { data: trackedInsights = [], isLoading } = useTrackedInsights()
  const untrackMutation = useUntrackInsight()
  const updateEntryPriceMutation = useUpdateEntryPrice()
  const [selectedInsight, setSelectedInsight] = useState<TrackedInsight | null>(
    null
  )

  // Extract unique tickers from tracked insights
  const tickers = useMemo(() => {
    return [
      ...new Set(
        trackedInsights
          .map((t) => t.insight.ticker)
          .filter((t): t is string => !!t)
      ),
    ]
  }, [trackedInsights])

  // Fetch stock quotes for all tickers
  const {
    data: quotes,
    isLoading: quotesLoading,
    refetch: refetchQuotes,
  } = useStockQuotes(tickers)

  const handleUntrack = async (e: React.MouseEvent, insightId: string) => {
    e.stopPropagation()
    try {
      await untrackMutation.mutateAsync(insightId)
      toast.success(t('insightUntracked'))
    } catch (error) {
      console.error('Failed to untrack:', error)
      toast.error(t('failedToUntrackInsight'))
    }
  }

  const sentimentConfig = {
    bullish: {
      icon: TrendingUp,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/20',
    },
    bearish: {
      icon: TrendingDown,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-500/20',
    },
    neutral: {
      icon: Minus,
      color: 'text-neutral-400',
      bg: 'bg-neutral-500/10',
      border: 'border-neutral-500/20',
    },
  }

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="h-[90vh] bg-neutral-950 border-white/[0.06] p-0 overflow-hidden flex flex-col">
        {/* Header */}
        <SheetHeader className="p-4 border-b border-white/[0.06]">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-white flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-amber-400" />
              {t('trackedInsights')}
              {trackedInsights.length > 0 && (
                <span className="text-sm text-neutral-500">
                  ({trackedInsights.length})
                </span>
              )}
            </SheetTitle>
            <div className="flex items-center gap-1">
              {tickers.length > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => refetchQuotes()}
                  disabled={quotesLoading}
                  className="text-neutral-400 hover:text-white"
                  title={t('refreshPrices')}
                >
                  <RefreshCw
                    className={cn('w-4 h-4', quotesLoading && 'animate-spin')}
                  />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="text-neutral-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
            </div>
          ) : trackedInsights.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mb-4">
                <Bookmark className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t('noTrackedInsights')}
              </h3>
              <p className="text-sm text-neutral-400 max-w-xs">
                {t('trackInsightsDescription')}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/[0.06]">
              {trackedInsights.map((track) => {
                const config = sentimentConfig[track.insight.sentiment]
                const SentimentIcon = config.icon
                const ticker = track.insight.ticker?.toUpperCase()
                const quote = ticker ? quotes?.get(ticker) : null
                const gainLoss =
                  quote && track.priceAtTrack
                    ? calculateGainLoss(quote.currentPrice, track.priceAtTrack)
                    : null

                return (
                  <div
                    key={track.id}
                    className="p-4 hover:bg-white/[0.02] transition-colors cursor-pointer"
                    onClick={() => setSelectedInsight(track)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Sentiment Icon */}
                      <div
                        className={cn(
                          'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                          config.bg
                        )}
                      >
                        <SentimentIcon
                          className={cn('w-4 h-4', config.color)}
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Ticker & Current Price */}
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {ticker && (
                            <span className="text-cyan-400 font-mono text-sm font-semibold">
                              ${ticker}
                            </span>
                          )}
                          {quote && (
                            <span className="text-white font-mono text-sm">
                              ${quote.currentPrice.toFixed(2)}
                            </span>
                          )}
                          {gainLoss && (
                            <span
                              className={cn(
                                'text-xs font-mono px-1.5 py-0.5 rounded',
                                gainLoss.percent >= 0
                                  ? 'text-emerald-400 bg-emerald-500/10'
                                  : 'text-red-400 bg-red-500/10'
                              )}
                            >
                              {gainLoss.percent >= 0 ? '+' : ''}
                              {gainLoss.percent.toFixed(2)}%
                            </span>
                          )}
                        </div>

                        {/* Tracked time */}
                        <div className="text-xs text-neutral-500 mb-1">
                          {t('tracked')}{' '}
                          {formatDistanceToNow(track.trackedAt, {
                            addSuffix: true,
                            locale: getDateLocale(i18n.language),
                          })}
                        </div>

                        {/* Content Preview */}
                        <p className="text-sm text-neutral-300 line-clamp-2 mb-2">
                          <TranslatableText text={track.insight.content} />
                        </p>

                        {/* Price info row */}
                        <div className="flex items-center gap-3 text-xs">
                          {track.priceAtTrack && (
                            <div className="text-neutral-500">
                              {t('entryLabel')}{' '}
                              <span className="text-neutral-400 font-mono">
                                ${track.priceAtTrack.toFixed(2)}
                              </span>
                            </div>
                          )}
                          {gainLoss && (
                            <div
                              className={
                                gainLoss.percent >= 0
                                  ? 'text-emerald-400'
                                  : 'text-red-400'
                              }
                            >
                              {gainLoss.percent >= 0 ? '↑' : '↓'}{' '}
                              <span className="font-mono">
                                ${Math.abs(gainLoss.value).toFixed(2)}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Author */}
                        <div className="text-xs text-neutral-500 mt-1">
                          {t('byAuthor')}{' '}
                          <span className="text-neutral-400">
                            {track.insight.displayName ||
                              track.insight.username}
                          </span>
                        </div>
                      </div>

                      {/* Untrack Button */}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => handleUntrack(e, track.insightId)}
                        disabled={untrackMutation.isPending}
                        className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 flex-shrink-0"
                      >
                        <Bookmark className="w-4 h-4 fill-current" />
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </SheetContent>

      {/* Insight Detail Sheet */}
      <InsightDetailSheet
        open={selectedInsight !== null}
        onClose={() => setSelectedInsight(null)}
        insight={
          selectedInsight
            ? {
                id: selectedInsight.insight.id,
                content: selectedInsight.insight.content,
                ticker: selectedInsight.insight.ticker,
                sentiment: selectedInsight.insight.sentiment,
                username: selectedInsight.insight.username,
                displayName: selectedInsight.insight.displayName,
                createdAt: selectedInsight.insight.createdAt,
                imageUrl: selectedInsight.insight.imageUrl,
                tags: selectedInsight.insight.tags,
              }
            : null
        }
        priceAtTrack={selectedInsight?.priceAtTrack}
        trackedAt={selectedInsight?.trackedAt}
        isTracked={true}
        onTrackToggle={async () => {
          if (selectedInsight) {
            try {
              await untrackMutation.mutateAsync(selectedInsight.insightId)
              toast.success(t('insightUntracked'))
              setSelectedInsight(null)
            } catch (error) {
              console.error('Failed to untrack:', error)
              toast.error(t('failedToUntrackInsight'))
            }
          }
        }}
        onAskAI={() => {
          if (selectedInsight) {
            onAskAI?.(
              selectedInsight.insight.id,
              selectedInsight.insight.ticker || undefined,
              selectedInsight.insight.sentiment,
              selectedInsight.insight.content
            )
            setSelectedInsight(null)
          }
        }}
        onUpdateEntryPrice={async (price) => {
          if (selectedInsight) {
            try {
              await updateEntryPriceMutation.mutateAsync({
                insightId: selectedInsight.insightId,
                price,
              })
              // Update local state to reflect the change immediately
              setSelectedInsight({
                ...selectedInsight,
                priceAtTrack: price,
              })
              toast.success(t('entryPriceUpdated'))
            } catch (error) {
              console.error('Failed to update entry price:', error)
              toast.error(t('failedToUpdateEntryPrice'))
            }
          }
        }}
      />
    </Sheet>
  )
}
